import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { newsApi, type ArticleSummary } from "../api/news";
import { formatDate } from "../lib/format";
import { Loading } from "./Loading";

/**
 * Dashboard widget — STORY-1.9.2. Renders the top N visible articles for
 * the caller's roles (filtered server-side via visible_to_roles).
 */
export function LatestNewsWidget({ limit = 3 }: { limit?: number }) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<ArticleSummary[] | null>(null);

  useEffect(() => {
    void newsApi
      .latest(limit, i18n.resolvedLanguage ?? "en")
      .then(setItems)
      .catch(() => setItems([]));
  }, [limit, i18n.resolvedLanguage]);

  if (items === null) {
    return (
      <section style={card}>
        <header style={head}>
          <strong>{t("news.latest", "Latest news")}</strong>
          <Link to="/news" className="widget-more-link" style={moreLink}>{t("news.viewAll", "View all →")}</Link>
        </header>
        <Loading />
      </section>
    );
  }
  if (items.length === 0) {
    return (
      <section style={card}>
        <header style={head}>
          <strong>{t("news.latest", "Latest news")}</strong>
          <Link to="/news" className="widget-more-link" style={moreLink}>{t("news.viewAll", "View all →")}</Link>
        </header>
        <p style={{ color: "#9ca3af", fontSize: 13, margin: 0 }}>{t("news.empty", "No news yet.")}</p>
      </section>
    );
  }

  return (
    <section style={card}>
      <header style={head}>
        <strong>{t("news.latest", "Latest news")}</strong>
        <Link to="/news" className="widget-more-link" style={moreLink}>{t("news.viewAll", "View all →")}</Link>
      </header>
      <ul style={list}>
        {items.map((a) => (
          <li key={a.id}>
            <Link to={`/news/${a.slug}`} style={itemLink}>
              <strong style={{ fontSize: 14, color: "#0b3a6f" }}>{a.title}</strong>
              {a.excerpt && (
                <span style={{ color: "#6b7280", fontSize: 12, display: "block", marginTop: 2 }}>
                  {a.excerpt.length > 120 ? a.excerpt.slice(0, 120) + "…" : a.excerpt}
                </span>
              )}
              {a.publishedAt && (
                <small style={{ color: "#9ca3af", display: "block", marginTop: 4 }}>
                  {formatDate(a.publishedAt)}
                </small>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

const card: React.CSSProperties = {
  background: "#fff", padding: 16, borderRadius: 10, boxShadow: "0 1px 2px rgba(0,0,0,.04)",
  display: "flex", flexDirection: "column", gap: 12,
};
const head: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", color: "#1f2937", fontSize: 14 };
const list: React.CSSProperties = { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 };
const itemLink: React.CSSProperties = { textDecoration: "none", display: "block", padding: 8, borderRadius: 6, transition: "background 120ms" };
const moreLink: React.CSSProperties = { color: "#0b3a6f", fontSize: 12, textDecoration: "none" };
